import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { VbPageHeader } from "@/components/vb";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { projectsInstance } from "@/services/ProjectsService";
import { Loader } from "lucide-react";

type CompanyItem = {
    company_id: number;
    company_name: string;
    status: "completed" | "in_progress" | string;
    lastReportId?: number | null;
    created_ts?: string;
};

const ProjectsList: React.FC = () => {
    const { token } = useAuth();
    const tokenRef = useRef(token);
    tokenRef.current = token;
    const navigate = useNavigate();

    const [companies, setCompanies] = useState<CompanyItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [q, setQ] = useState("");

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const res = await projectsInstance.getCompanies(tokenRef.current);
                if (mounted) setCompanies(res ?? []);
            } catch {
                setErr("Failed to load projects");
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    const filtered = useMemo(
        () =>
            companies
                .slice()
                .sort((a, b) => a.company_name.localeCompare(b.company_name))
                .filter((c) => c.company_name.toLowerCase().includes(q.toLowerCase())),
        [companies, q]
    );

    const handleClick = (c: CompanyItem) => {
        if (c.status === "completed" && c.lastReportId) {
            navigate(`/reports/${c.company_id}/${c.lastReportId}`);
        }
    };

    return (
        <div className="vb-page space-y-6">
            <VbPageHeader title="Projects List" />
            <div className="max-w-md">
                <Input
                    placeholder="Search projects..."
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                />
            </div>

            {loading && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Loader className="h-4 w-4 animate-spin" /> Loading projects…
                </div>
            )}

            {err && <div className="text-sm text-red-600">{err}</div>}

            {!loading && !err && filtered.length === 0 && (
                <div className="text-sm text-vb-text-3">No projects found.</div>
            )}

            <ul className="grid gap-3">
                {filtered.map((c, i) => {
                    const canOpen = c.status === "completed" && !!c.lastReportId;
                    const imgIndex = (i % 10) + 1;
                    return (
                        <li
                            key={`${c.company_id}-${c.lastReportId ?? "none"}`}
                            onClick={() => canOpen && handleClick(c)}
                            className={[
                                "flex gap-4 p-4 border rounded transition",
                                canOpen
                                    ? "hover:bg-gray-50 cursor-pointer"
                                    : "opacity-70 cursor-not-allowed",
                            ].join(" ")}
                        >
                            <div
                                className="flex items-center justify-center w-[64px] h-[64px] flex-shrink-0 bg-center bg-cover rounded"
                                style={{ backgroundImage: `url(/report/${imgIndex}.png)` }}
                                aria-hidden
                            />
                            <div className="flex-1">
                                <div className="font-medium">{c.company_name}</div>
                                <div className="text-sm text-vb-text-3">
                                    {c.status === "completed" && c.lastReportId
                                        ? "Open latest report"
                                        : c.status === "in_progress"
                                            ? "Generating…"
                                            : "No completed reports yet"}
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default ProjectsList;
